// Shared primitives for explicit (non-autocaptured) client analytics events.
//
// Extracted from aiShareAnalytics.ts when a second family of deliberate events
// arrived: every family needs the same production gate, the same undefined
// stripping, and the same `app` stamp, and three copies of that would drift.
// Add a family by giving it its own module with an event-name constant and a
// thin wrapper over captureClientEvent — not by re-implementing the gate.

import { capturePostHogEvent } from "@client/telemetry/posthog";
import { isProductAnalyticsEnvironment } from "./environment";

export type AnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | string[];
export type AnalyticsProperties = Record<
  string,
  AnalyticsPropertyValue | undefined
>;

function omitUndefinedProperties(
  properties: AnalyticsProperties,
): Record<string, AnalyticsPropertyValue> {
  return Object.fromEntries(
    Object.entries(properties).filter(
      (entry): entry is [string, AnalyticsPropertyValue] =>
        entry[1] !== undefined,
    ),
  );
}

/**
 * Record one explicit product event.
 *
 * `sourceFamily` groups a feature's events so they can be found and filtered
 * together in analytics (`ai_share`, `round_sync`, ...).
 *
 * The production gate lives HERE rather than at the call sites: non-production
 * deployments initialize PostHog so feature flags can evaluate, so a live
 * client is not permission to record product usage — see
 * isProductAnalyticsEnvironment. Autocapture and pageviews are turned off by
 * config there, but an explicit capture has to check for itself, and a call
 * site that forgets silently pollutes the shared project with fixture data.
 */
export function captureClientEvent({
  event,
  sourceFamily,
  properties,
}: {
  event: string;
  sourceFamily: string;
  properties: AnalyticsProperties;
}): void {
  const stamped = omitUndefinedProperties({
    app: "s-process",
    source_family: sourceFamily,
    ...properties,
  });

  // Read at EMIT time, not module-load time — that's the whole point of routing
  // through a sink (see setClientEventSinkForTests).
  if (testSink != null) {
    testSink(event, stamped);
    return;
  }

  if (!isProductAnalyticsEnvironment()) {
    return;
  }

  capturePostHogEvent(event, stamped);
}

type ClientEventSink = (
  event: string,
  properties: Record<string, AnalyticsPropertyValue>,
) => void;

let testSink: ClientEventSink | null = null;

/**
 * Test seam: route explicit events to `sink` instead of PostHog, and bypass the
 * production gate (the sink IS the environment being asserted on). Pass `null` to
 * restore normal behavior — suites must do that in teardown, since this is
 * process-global like any module state.
 *
 * This exists because `mock.module` cannot do the job here. It is process-global
 * AND load-order dependent: an emitter binds its imported capture function when
 * its module is first evaluated, so if ANY earlier-loaded test file imports that
 * emitter (nine of them import SPAStorageAdapter), the mock registers too late
 * and silently captures nothing. That passes locally and fails in CI's file
 * order — which is exactly what happened. A sink read at emit time can't be
 * out-raced by an import.
 *
 * Same shape as `connectSentry` for the Sentry wrapper, and
 * `setBuildMarkerTimeoutForTests` for the corpus cache.
 */
export function setClientEventSinkForTests(sink: ClientEventSink | null): void {
  testSink = sink;
}
